'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import type { DetailTopBarIconName } from '@/components/DetailTopBarIconButton';

type SlotEntry<T> = {
  id: symbol;
  node: T;
};

export type MobileDetailTopBarAction = {
  key: string;
  label: string;
  icon: DetailTopBarIconName;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger' | 'success';
};

export type MobileDetailTopBarConfig = {
  title: string;
  backHref: string;
  actions?: MobileDetailTopBarAction[];
};

type PageActionsSlotContextValue = {
  register: (id: symbol, node: ReactNode) => void;
  unregister: (id: symbol) => void;
  registerRefresh: (id: symbol, node: ReactNode) => void;
  unregisterRefresh: (id: symbol) => void;
  registerUsecase: (id: symbol, node: ReactNode) => void;
  unregisterUsecase: (id: symbol) => void;
  registerMobileDetail: (id: symbol, config: MobileDetailTopBarConfig | null) => void;
  unregisterMobileDetail: (id: symbol) => void;
  registerHideTopBar: (id: symbol, hidden: boolean) => void;
  unregisterHideTopBar: (id: symbol) => void;
  subscribeHideTopBar: (listener: () => void) => () => void;
  getHideTopBar: () => boolean | null;
  subscribeMain: (listener: () => void) => () => void;
  subscribeRefresh: (listener: () => void) => () => void;
  subscribeUsecase: (listener: () => void) => () => void;
  subscribeMobileDetail: (listener: () => void) => () => void;
  getMainNode: () => ReactNode;
  getRefreshNode: () => ReactNode;
  getUsecaseNode: () => ReactNode;
  getMobileDetailConfig: () => MobileDetailTopBarConfig | null;
};

const noop = () => {};

const PageActionsSlotContext = createContext<PageActionsSlotContextValue>({
  register: noop,
  unregister: noop,
  registerRefresh: noop,
  unregisterRefresh: noop,
  registerUsecase: noop,
  unregisterUsecase: noop,
  registerMobileDetail: noop,
  unregisterMobileDetail: noop,
  registerHideTopBar: noop,
  unregisterHideTopBar: noop,
  subscribeHideTopBar: () => noop,
  getHideTopBar: () => null,
  subscribeMain: () => noop,
  subscribeRefresh: () => noop,
  subscribeUsecase: () => noop,
  subscribeMobileDetail: () => noop,
  getMainNode: () => null,
  getRefreshNode: () => null,
  getUsecaseNode: () => null,
  getMobileDetailConfig: () => null,
});

export const usePageActionsSlotContext = (): PageActionsSlotContextValue => {
  return useContext(PageActionsSlotContext);
};

const useStackRegistry = <T,>() => {
  const stackRef = useRef<SlotEntry<T>[]>([]);
  const listenersRef = useRef(new Set<() => void>());

  const emit = useCallback(() => {
    for (const listener of listenersRef.current) {
      listener();
    }
  }, []);

  const register = useCallback(
    (id: symbol, node: T) => {
      const previous = stackRef.current;
      const existingIndex = previous.findIndex((entry) => entry.id === id);
      if (existingIndex >= 0) {
        const existing = previous[existingIndex];
        if (existing.node === node && existingIndex === previous.length - 1) {
          return;
        }

        const without = previous.filter((entry) => entry.id !== id);
        stackRef.current = [...without, { id, node }];
        emit();
        return;
      }

      stackRef.current = [...previous, { id, node }];
      emit();
    },
    [emit],
  );

  const unregister = useCallback(
    (id: symbol) => {
      const previous = stackRef.current;
      const next = previous.filter((entry) => entry.id !== id);
      if (next.length === previous.length) {
        return;
      }

      stackRef.current = next;
      emit();
    },
    [emit],
  );

  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const getTopNode = useCallback(() => {
    const stack = stackRef.current;
    return stack.length > 0 ? stack[stack.length - 1].node : null;
  }, []);

  return { register, unregister, subscribe, getTopNode };
};

export const PageActionsSlotProvider = ({ children }: { children: ReactNode }) => {
  const main = useStackRegistry<ReactNode>();
  const refresh = useStackRegistry<ReactNode>();
  const usecase = useStackRegistry<ReactNode>();
  const mobileDetail = useStackRegistry<MobileDetailTopBarConfig | null>();
  const hideTopBar = useStackRegistry<boolean>();

  const value = useMemo<PageActionsSlotContextValue>(
    () => ({
      register: main.register,
      unregister: main.unregister,
      registerRefresh: refresh.register,
      unregisterRefresh: refresh.unregister,
      registerUsecase: usecase.register,
      unregisterUsecase: usecase.unregister,
      registerMobileDetail: mobileDetail.register,
      unregisterMobileDetail: mobileDetail.unregister,
      registerHideTopBar: hideTopBar.register,
      unregisterHideTopBar: hideTopBar.unregister,
      subscribeHideTopBar: hideTopBar.subscribe,
      getHideTopBar: hideTopBar.getTopNode,
      subscribeMain: main.subscribe,
      subscribeRefresh: refresh.subscribe,
      subscribeUsecase: usecase.subscribe,
      subscribeMobileDetail: mobileDetail.subscribe,
      getMainNode: main.getTopNode,
      getRefreshNode: refresh.getTopNode,
      getUsecaseNode: usecase.getTopNode,
      getMobileDetailConfig: mobileDetail.getTopNode,
    }),
    [
      main.register,
      main.unregister,
      main.subscribe,
      main.getTopNode,
      refresh.register,
      refresh.unregister,
      refresh.subscribe,
      refresh.getTopNode,
      usecase.register,
      usecase.unregister,
      usecase.subscribe,
      usecase.getTopNode,
      mobileDetail.register,
      mobileDetail.unregister,
      mobileDetail.subscribe,
      mobileDetail.getTopNode,
      hideTopBar.register,
      hideTopBar.unregister,
      hideTopBar.subscribe,
      hideTopBar.getTopNode,
    ],
  );

  return <PageActionsSlotContext.Provider value={value}>{children}</PageActionsSlotContext.Provider>;
};

type PageActionsSlotProps = {
  className?: string;
};

export const PageActionsSlot = ({ className }: PageActionsSlotProps) => {
  const { subscribeMain, getMainNode } = usePageActionsSlotContext();
  const topNode = useSyncExternalStore(subscribeMain, getMainNode, getMainNode);
  if (!topNode) {
    return null;
  }
  return <div className={className}>{topNode}</div>;
};

export const PageRefreshSlot = ({ className }: PageActionsSlotProps) => {
  const { subscribeRefresh, getRefreshNode } = usePageActionsSlotContext();
  const refreshNode = useSyncExternalStore(subscribeRefresh, getRefreshNode, getRefreshNode);
  if (!refreshNode) {
    return null;
  }

  return <div className={className}>{refreshNode}</div>;
};

export const PageUsecaseSlot = ({ className }: PageActionsSlotProps) => {
  const { subscribeUsecase, getUsecaseNode } = usePageActionsSlotContext();
  const usecaseNode = useSyncExternalStore(subscribeUsecase, getUsecaseNode, getUsecaseNode);
  if (!usecaseNode) {
    return null;
  }

  return <div className={className}>{usecaseNode}</div>;
};

export const useMobileDetailTopBarConfig = (): MobileDetailTopBarConfig | null => {
  const { subscribeMobileDetail, getMobileDetailConfig } = usePageActionsSlotContext();
  return useSyncExternalStore(subscribeMobileDetail, getMobileDetailConfig, getMobileDetailConfig);
};

/**
 * 마운트되어 있는 동안 상단바(TopBar) 숨김을 요청한다.
 * 페이지에서 `useHideTopBar(isLoading || isOnboarding)` 식으로 호출한다.
 */
export const useHideTopBar = (hidden: boolean): void => {
  const { registerHideTopBar, unregisterHideTopBar } = usePageActionsSlotContext();
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) {
    idRef.current = Symbol('useHideTopBar');
  }

  useEffect(() => {
    const id = idRef.current!;
    if (hidden) {
      registerHideTopBar(id, true);
    } else {
      unregisterHideTopBar(id);
    }
    return () => unregisterHideTopBar(id);
  }, [hidden, registerHideTopBar, unregisterHideTopBar]);
};

const useIsTopBarHidden = (): boolean => {
  const { subscribeHideTopBar, getHideTopBar } = usePageActionsSlotContext();
  return Boolean(useSyncExternalStore(subscribeHideTopBar, getHideTopBar, getHideTopBar));
};

/**
 * 자식 TopBar를 감싸, 페이지가 `useHideTopBar(true)`를 요청하면 children 대신
 * fallback(없으면 아무것도)을 렌더한다. (예: 온보딩에서 최소 계정 메뉴만 노출)
 * PageActionsSlotProvider 내부에서 사용해야 컨텍스트를 읽을 수 있다.
 */
export const HideableTopBar = ({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) => {
  const hidden = useIsTopBarHidden();
  return <>{hidden ? fallback : children}</>;
};
